import React from 'react';
import PropTypes from 'prop-types';
import { FormControl, InputLabel, Select } from '@mui/material';
import { styled } from '@mui/material/styles';

// https://github.com/mui/material-ui/issues/29207
const StyledSelect = styled(Select)(({ ownerState, theme }) => ({
  '& .MuiNativeSelect-select': {
    height: 'auto',
    lineHeight: theme.spacing(2.5),
    margin: 0,
    minWidth: theme.spacing(2),
    padding: theme.spacing(1.5),
    textOverflow: 'ellipsis',
    ...ownerState,
  },
}));

const StyledInputLabel = styled(InputLabel)(({ ownerState, theme }) => ({
  '&.MuiInputLabel-root': {
    ...ownerState,
  },
}));

/**
 *
 * @param {string} label - kebab-case string to be used as id and formatted into Label in SelectMenu
 * @returns Title Case string to be used as visible label for Select
 * eg: test-label to Test Label
 */
const getLabelFormat = (label) => {
  return label
    .split('-')
    .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
    .join(' ');
};

const SelectMenu = ({
  label,
  onChange,
  optionProps,
  SELECT_OPTIONS,
  stylePropsSelect = {},
  stylePropsLabel = {},
  value,
  selectInputProps = {},
  ...props
}) => {
  const labelDisplay = getLabelFormat(label);

  return (
    <FormControl fullWidth variant="outlined" {...props}>
      <StyledInputLabel
        htmlFor={`${label}-select`}
        ownerState={stylePropsLabel}
      >
        {labelDisplay}
      </StyledInputLabel>
      <StyledSelect
        inputProps={{
          id: `${label}-select`,
          ...selectInputProps,
        }}
        label={labelDisplay}
        native
        onChange={(e) =>
          onChange(!isNaN(e.target.value) ? +e.target.value : e.target.value)
        }
        ownerState={stylePropsSelect}
        value={value}
        variant="outlined"
      >
        {SELECT_OPTIONS.map((option, idx) => (
          <option
            key={`${option.label}-${idx}`}
            value={option.value}
            {...(optionProps ? optionProps : null)}
          >
            {option.label}
          </option>
        ))}
      </StyledSelect>
    </FormControl>
  );
};

SelectMenu.propTypes = {
  /**
   * Dash separated id for the select component, example: `test-label`
   * Also converted into string for visual display, example: `Test Label`
   */
  label: PropTypes.string.isRequired,
  /**
   * Function to fire on changing selected value
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Pass props to the options element(s)
   */
  optionProps: PropTypes.object,
  /**
   * List of items the select will use to create the dropdown options.
   */
  SELECT_OPTIONS: PropTypes.array.isRequired,
  /**
   * Style overrides or add on for select and label
   */
  stylePropsSelect: PropTypes.object,
  stylePropsLabel: PropTypes.object,
  /**
   * Additional props to pass to the select input element
   */
  selectInputProps: PropTypes.object,
  /**
   * The input value, providing an empty string will select no options.
   */
  value: PropTypes.node.isRequired,
};

export default SelectMenu;
